import React, { useState, useRef, useEffect } from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { classes } from '~utils/ClassNames';
import {
    StyledTooltipButton,
    StyledTooltipHiddenText,
    StyledTooltipTextWrapper
} from './TooltipStyles';

export type ITooltipPlacement =
    | 'top'
    | 'top-left'
    | 'top-right'
    | 'bottom'
    | 'bottom-left'
    | 'bottom-right';

export type ITooltipType =
    | 'default'
    | 'alert';

export interface ITooltip {
    className?: string;
    children: React.ReactChild;
    hiddenText: string;
    hiddenTextId?: string;
    disabled?: boolean;
    placement?: ITooltipPlacement;
    type?: ITooltipType;
}

const cartTooltipButtonClass = classes('cart-tooltip-button');
const cartTooltipHiddenTextWrapperClass = classes('cart-tooltip-hidden-text-wrapper');
const cartTooltipHiddenTextClass = classes('cart-tooltip-hidden-text');

const Tooltip: React.FC<ITooltip> = observer((props) => {
    const {
        children,
        className,
        disabled,
        hiddenText,
        placement,
        type = 'default'
    } = props;

    const buttonRef = useRef<HTMLButtonElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [tooltipDirection, setTooltipPosition] = useState(placement || 'top');
    const [tooltipOffset, setTooltipOffset] = useState({ top: 'auto', bottom: 'auto' });

    const onShowTooltipEvent = action(() => {
        setIsTooltipVisible(true);
    });

    const onHideTooltipEvent = action((event: React.MouseEvent) => {
        if (event.currentTarget.contains(event.relatedTarget as Node)) {
            return;
        }
        setIsTooltipVisible(false);
    });

    const handleEscKeyDown = action((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            setIsTooltipVisible(false);
        }
    });

    useEffect(() => {
        document.addEventListener('keydown', handleEscKeyDown);

        return () => {
            document.removeEventListener('keydown', handleEscKeyDown);
        };
    }, []);

    useEffect(() => {
        if (isTooltipVisible && buttonRef.current && tooltipRef.current) {
            const triggerRect = buttonRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const style = window.getComputedStyle(tooltipRef.current, '::before');
            const tooltipArrowHeight = parseFloat(style.getPropertyValue('height'));

            switch (tooltipDirection) {
                case 'top':
                case 'top-left':
                case 'top-right':
                    if (tooltipRect.width + tooltipRect.x > window.innerWidth) {
                        setTooltipPosition('top-left');
                    } else if (tooltipRect.x < 0) {
                        setTooltipPosition('top-right');
                    }
                    const bottomOffset = triggerRect.height + tooltipArrowHeight;
                    setTooltipOffset({ top: 'auto', bottom: `${bottomOffset}px` });
                    break;
                default:
                    if (tooltipRect.width + tooltipRect.x > window.innerWidth) {
                        setTooltipPosition('bottom-left');
                    } else if (tooltipRect.x < 0) {
                        setTooltipPosition('bottom-right');
                    }

                    const topOffset = triggerRect.height + tooltipArrowHeight;
                    setTooltipOffset({ top: `${topOffset}px`, bottom: 'auto' });
            }
        }
    }, [isTooltipVisible]);

    return (
        <StyledTooltipButton
            ref={buttonRef}
            type="button"
            className={`${cartTooltipButtonClass} ${className || ''}`}
            onFocus={onShowTooltipEvent}
            onBlur={() => setIsTooltipVisible(false)}
            onMouseEnter={onShowTooltipEvent}
            onMouseLeave={onHideTooltipEvent}
        >
            {children}
            {isTooltipVisible && !disabled && (
                <StyledTooltipTextWrapper
                    ref={tooltipRef}
                    className={`${cartTooltipHiddenTextWrapperClass} placement-${tooltipDirection} type-${type}`}
                    style={{ top: tooltipOffset.top, bottom: tooltipOffset.bottom }}
                    role="tooltip"
                >
                    <StyledTooltipHiddenText
                        className={cartTooltipHiddenTextClass}
                    >
                        {hiddenText}
                    </StyledTooltipHiddenText>
                </StyledTooltipTextWrapper>
            )}
        </StyledTooltipButton>
    );
});

export default Tooltip;
